import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../i18n/languages';
import './LanguageSelector.css';

/**
 * Language selector — sits in the top header next to the station selector.
 *
 * Lists every language registered in ../i18n/languages.js. Picking one calls
 * i18n.changeLanguage(), which:
 *   • re-renders the whole UI instantly (no page refresh), and
 *   • persists the choice to localStorage (see ../i18n/index.js), so it
 *     survives refreshes and future sessions.
 *
 * Adding a new language never requires touching this component — it renders
 * whatever LANGUAGES contains.
 */
export default function LanguageSelector() {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const current =
    LANGUAGES.find((l) => l.code === i18n.language) ||
    LANGUAGES.find((l) => i18n.language && i18n.language.startsWith(l.code)) ||
    LANGUAGES[0];

  const pick = (code) => {
    if (code !== i18n.language) i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div className="language-selector" ref={ref}>
      <button
        className="language-selector-button"
        onClick={() => setOpen(!open)}
        type="button"
        title={t('header.language')}
        aria-label={t('header.language')}
      >
        <span className="ls-icon">🌐</span>
        <span className="ls-name">{current?.nativeLabel}</span>
        <span className="ls-chev">{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div className="language-selector-menu">
          <div className="ls-section-label">{t('header.language')}</div>
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              className={`ls-item ${l.code === current?.code ? 'ls-active' : ''}`}
              onClick={() => pick(l.code)}
              type="button"
            >
              <span className="ls-item-flag">{l.flag}</span>
              <span className="ls-item-name">{l.nativeLabel}</span>
              <span className="ls-item-sub">{l.label}</span>
              {l.code === current?.code && <span className="ls-check">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
